import { DocumentSnapshot } from "firebase/firestore";
import { saveAs } from "file-saver";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

const formatICSDate = (date: Date): string => {
  // First convert the date to UTC from Zurich time
  const utcDate = zonedTimeToUtc(date, "Europe/Zurich");
  // Then format it with Z to indicate it's UTC
  return formatInTimeZone(utcDate, "UTC", "yyyyMMdd'T'HHmmss'Z'");
};

const escapeICSText = (text: string | undefined): string => {
  if (!text) return "";
  return text
    .replace(/[\\,;]/g, (match) => "\\" + match)
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r");
};

// RFC 5545: content lines must not exceed 75 octets. Long lines are folded
// with CRLF + single space. Outlook is strict about this — unfolded lines
// get truncated or dropped on edit/save.
const foldLine = (line: string): string => {
  const MAX_OCTETS = 75;
  const encoder = new TextEncoder();
  const decoder = new TextDecoder("utf-8");
  const bytes = encoder.encode(line);
  if (bytes.length <= MAX_OCTETS) return line;

  const chunks: string[] = [];
  let offset = 0;
  let isFirst = true;
  while (offset < bytes.length) {
    // Continuation lines begin with a leading space, which counts toward the 75-octet limit.
    const limit = isFirst ? MAX_OCTETS : MAX_OCTETS - 1;
    let end = Math.min(offset + limit, bytes.length);
    // Back off so we don't split a UTF-8 multibyte sequence.
    while (end > offset && end < bytes.length && (bytes[end] & 0xc0) === 0x80) {
      end--;
    }
    chunks.push(decoder.decode(bytes.slice(offset, end)));
    offset = end;
    isFirst = false;
  }
  return chunks.join("\r\n ");
};

export const generateICSContent = (
  appointment: DocumentSnapshot,
  object: DocumentSnapshot,
  project: DocumentSnapshot
): string | null => {
  const appointmentData = appointment.data();
  const objectData = object.data();
  const projectData = project.data();

  if (
    !appointmentData ||
    !objectData ||
    !projectData ||
    !appointmentData.appointmentDate
  ) {
    return null;
  }

  const startDate = appointmentData.appointmentDate.toDate();
  const duration = appointmentData.duration || 60; // default 60 minutes if not set
  const endDate = new Date(startDate.getTime() + duration * 60 * 1000);

  const objectUrl = `https://app.rissapp.ch/admin/projects/${project.id}/${object.id}`;

  // Build detailed description with all required information.
  // The URL is embedded here (rather than as a top-level URL property) because
  // Outlook strips the top-level URL property on edit/save.
  const description = [
    `Projektnummer: ${escapeICSText(projectData.projectNumber)}`,
    `Projektname: ${escapeICSText(projectData.title)}`,
    `Baustelle: ${escapeICSText(projectData.constructionSite)}`,
    "",
    `Objekt: ${escapeICSText(objectData.title)}`,
    `Standort / Adresse: ${escapeICSText(objectData.address)}`,
    `Grundstücksnummer: ${escapeICSText(objectData.propertyNumber)}`,
    `Eigentümer: ${escapeICSText(objectData.owner)}`,
    "",
    appointmentData.notes
      ? `Notizen: ${escapeICSText(appointmentData.notes)}`
      : "",
    "",
    `Link: ${objectUrl}`,
  ]
    .filter((line, idx, arr) => line !== "" || (idx > 0 && arr[idx - 1] !== ""))
    .join("\\n");

  const startTime = formatInTimeZone(startDate, "Europe/Zurich", "H:mm");
  const address = objectData.address?.trim() || projectData.projectNumber?.trim() || "";
  const summary = address
    ? `Rissprotokoll ${address} (Termin: ${startTime} Uhr)`
    : `Rissprotokoll (Termin: ${startTime} Uhr)`;
  const dtstamp = formatICSDate(new Date());

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Vermflex//Rissprotokoll//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${appointment.id}@vermflex.com`,
    `DTSTAMP:${dtstamp}`,
    `CREATED:${dtstamp}`,
    `LAST-MODIFIED:${dtstamp}`,
    "SEQUENCE:0",
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${escapeICSText(summary)}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${escapeICSText(objectData.address || "")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return lines.map(foldLine).join("\r\n");
};

export const exportToCalendar = (
  appointment: DocumentSnapshot,
  object: DocumentSnapshot,
  project: DocumentSnapshot
): void => {
  const icsContent = generateICSContent(appointment, object, project);

  if (!icsContent) {
    console.error("Could not generate calendar event: missing required data");
    return;
  }

  // Prepend a UTF-8 BOM so older Outlook (Windows desktop, pre-2019) doesn't
  // misinterpret umlauts as Windows-1252 and mangle them.
  const blob = new Blob(["﻿", icsContent], {
    type: "text/calendar;charset=utf-8",
  });
  const fileName = `Rissprotokoll-Termin-${appointment.id}.ics`;
  saveAs(blob, fileName);
};
